import { useState, useEffect } from 'preact/hooks'
import { apiClient, authStore, InitData, User } from '../../core'
import { LoginWidget } from '../login/LoginWidget'
import { t } from '../../core/i18n'

interface Waitlist {
  id: string
  name: string
  description: string | null
  slug: string | null
  waitingCount: number
  seasonClass: {
    id: string
    name: string
    dayOfWeek: string | null
    time: string | null
    location: string | null
    priceDkk: number
    seasonName: string
  } | null
}

interface WaitlistsWidgetProps {
  initData: InitData
}

/**
 * WaitlistsWidget — informational, public widget that lists open waitlists.
 *
 * Mirrors the CMS pattern (`apps/holdstyring/src/components/cms/widgets/WaitlistsWidget.tsx`):
 *   - No auth-gating, no leave-action; embedded widgets cannot manage entries.
 *   - Each card shows class meta (day · time · location), waiting count, and a
 *     CTA link that takes the visitor to the tenant's booking page where they
 *     can actually sign up / manage their waitlist entries.
 */
export function WaitlistsWidget({ initData }: WaitlistsWidgetProps) {
  const s = t(initData.locale)
  const [waitlists, setWaitlists] = useState<Waitlist[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [user, setUser] = useState<User | null>(authStore.getUser())
  // Which waitlist the visitor is joining, and the outcome per waitlist id.
  const [loginFor, setLoginFor] = useState<Waitlist | null>(null)
  const [joining, setJoining] = useState<string | null>(null)
  const [joined, setJoined] = useState<Record<string, number>>({})
  const [joinError, setJoinError] = useState<Record<string, string>>({})

  useEffect(() => authStore.subscribe(setUser), [])

  // Logging in inside the modal should finish what the visitor started: close
  // the dialog and join the waitlist they clicked, rather than making them
  // find the button again.
  useEffect(() => {
    if (!user || !loginFor) return
    const pending = loginFor
    setLoginFor(null)
    void join(pending)
    // Intentionally keyed on `user` alone: this should fire exactly once, when
    // the visitor transitions from logged-out to logged-in.
  }, [user])

  useEffect(() => {
    apiClient.get<Waitlist[]>('/api/widget/waitlists').then(result => {
      if (result.error) {
        setError(result.error)
      } else if (result.data) {
        setWaitlists(result.data)
      }
      setLoading(false)
    })
  }, [])

  async function join(wl: Waitlist) {
    if (joining) return
    // Anonymous visitors log in inline; the authStore subscription above then
    // re-renders and they press join again with a session.
    if (!authStore.getUser()) {
      setLoginFor(wl)
      return
    }
    setJoining(wl.id)
    setJoinError(prev => ({ ...prev, [wl.id]: '' }))

    const result = await apiClient.post<{ success?: boolean; position?: number }>(
      '/api/widget/waitlists',
      { waitlistId: wl.id },
      true,
    )
    setJoining(null)

    if (result.error) {
      if (result.code === 'AUTH_REQUIRED') {
        authStore.clearTokens()
        setLoginFor(wl)
        return
      }
      setJoinError(prev => ({
        ...prev,
        [wl.id]: result.code === 'ALREADY_ON_WAITLIST' ? s.alreadyOnWaitlist : result.error!,
      }))
      return
    }

    setJoined(prev => ({ ...prev, [wl.id]: result.data?.position ?? 0 }))
    setWaitlists(prev => prev.map(x =>
      x.id === wl.id ? { ...x, waitingCount: x.waitingCount + 1 } : x
    ))
  }

  // Loading — skeleton cards
  if (loading) {
    return (
      <div>
        <style>{waitlistStyles}</style>
        <div class="hsw-waitlist-list">
          {[0, 1, 2].map(i => (
            <div key={i} class="hsw-skeleton-card">
              <div class="hsw-skeleton hsw-sk-meta" />
              <div class="hsw-skeleton hsw-sk-name" />
              <div class="hsw-skeleton hsw-sk-desc" />
            </div>
          ))}
        </div>
      </div>
    )
  }

  // Error
  if (error) {
    return (
      <div>
        <style>{waitlistStyles}</style>
        <div class="hsw-error" role="alert">
          <span class="hsw-error-icon" aria-hidden="true">⚠️</span>
          <span>{error}</span>
        </div>
      </div>
    )
  }

  // Empty
  if (!waitlists.length) {
    return (
      <div>
        <style>{waitlistStyles}</style>
        <div class="hsw-empty">
          <div class="hsw-empty-icon" aria-hidden="true">⏳</div>
          <p class="hsw-empty-text">{s.noWaitlists}</p>
        </div>
      </div>
    )
  }

  return (
    <div>
      <style>{waitlistStyles}</style>
      <div class="hsw-waitlist-list">
        {waitlists.map(wl => {
          const sc = wl.seasonClass
          const className = sc?.name || wl.name
          const metaParts: string[] = []
          if (sc?.dayOfWeek) metaParts.push(sc.dayOfWeek)
          if (sc?.time) metaParts.push(sc.time)
          if (sc?.location) metaParts.push(sc.location)
          const meta = metaParts.join(' · ')

          return (
            <div key={wl.id} class="hsw-waitlist-card">
              <div class="hsw-waitlist-main">
                {meta && <div class="hsw-waitlist-meta">{meta}</div>}
                <div class="hsw-waitlist-name">{className}</div>
                {wl.description && (
                  <div class="hsw-waitlist-desc">{wl.description}</div>
                )}
              </div>
              <div class="hsw-waitlist-actions">
                <span class="hsw-position-badge">
                  {wl.waitingCount} {s.onWaitlist}
                </span>
                {joined[wl.id] !== undefined ? (
                  <span class="hsw-waitlist-joined">
                    ✓ {s.waitlistJoinedTitle}
                    {joined[wl.id] > 0 && ` · ${s.yourPosition} ${joined[wl.id]}`}
                  </span>
                ) : (
                  <button
                    type="button"
                    class="hsw-cta-link"
                    onClick={() => join(wl)}
                    disabled={joining === wl.id}
                  >
                    {joining === wl.id ? '...' : s.joinWaitlistCta}
                  </button>
                )}
                {joinError[wl.id] && (
                  <span class="hsw-waitlist-error" role="alert">{joinError[wl.id]}</span>
                )}
              </div>
            </div>
          )
        })}
      </div>

      {/* Inline login — an anonymous visitor never leaves the embedding site.
          LoginWidget writes to authStore; the subscription above re-renders. */}
      {loginFor && (
        <div class="hsw-wl-modal-backdrop" onClick={() => setLoginFor(null)}>
          <div
            class="hsw-wl-login-modal"
            role="dialog"
            aria-modal="true"
            onClick={e => e.stopPropagation()}
          >
            <button
              type="button"
              class="hsw-wl-modal-close"
              aria-label={s.close}
              onClick={() => setLoginFor(null)}
            >
              ×
            </button>
            <p class="hsw-wl-login-intro">
              {s.loginOrSignUpToJoin}{' '}
              <strong>{loginFor.seasonClass?.name || loginFor.name}</strong>.
            </p>
            <LoginWidget initData={initData} />
          </div>
        </div>
      )}
    </div>
  )
}

const waitlistStyles = `
  .hsw-waitlist-joined { font-size: 0.875rem; font-weight: 600; color: #15803d; }
  .hsw-waitlist-error { font-size: 0.8125rem; color: #b91c1c; }
  button.hsw-cta-link { border: none; cursor: pointer; font: inherit; }
  button.hsw-cta-link:disabled { opacity: 0.6; cursor: default; }
  .hsw-wl-modal-backdrop {
    position: fixed; inset: 0; background: rgba(0,0,0,0.5);
    display: flex; align-items: center; justify-content: center; padding: 1rem; z-index: 9999;
  }
  .hsw-wl-login-modal {
    position: relative; background: #fff; border-radius: 16px; padding: 1.5rem;
    width: 100%; max-width: 460px; max-height: 90vh; overflow-y: auto;
    box-shadow: 0 20px 40px rgba(0,0,0,0.2);
  }
  .hsw-wl-modal-close {
    position: absolute; top: 0.75rem; right: 0.75rem; border: none; background: none;
    font-size: 1.5rem; line-height: 1; cursor: pointer; color: #737373;
  }
  .hsw-wl-login-intro { margin: 0 0 1rem; padding-right: 1.5rem; color: #404040; }

  .hsw-waitlist-list {
    display: flex;
    flex-direction: column;
    gap: 1rem;
    max-width: 900px;
    margin: 0 auto;
  }
  .hsw-waitlist-card {
    background: var(--yoga-card-bg, #ffffff);
    border: 1px solid var(--yoga-card-border, #e5e7eb);
    border-radius: var(--yoga-radius, 0);
    padding: 1.5rem;
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 1.5rem;
    transition: border-color .2s, box-shadow .2s;
  }
  .hsw-waitlist-card:hover {
    border-color: rgba(0,0,0,0.2);
    box-shadow: 0 2px 12px rgba(0,0,0,0.05);
  }
  .hsw-waitlist-main {
    flex: 1;
    min-width: 0;
  }
  .hsw-waitlist-meta {
    font-size: 0.75rem;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    color: var(--yoga-text-muted, #737373);
    font-weight: 500;
    margin-bottom: 0.4rem;
  }
  .hsw-waitlist-name {
    font-size: 1.1rem;
    font-weight: 700;
    color: var(--yoga-text, #0a0a0a);
    line-height: 1.3;
    letter-spacing: -0.01em;
  }
  .hsw-waitlist-desc {
    margin-top: 0.5rem;
    font-size: 0.9rem;
    color: var(--yoga-text-muted, #525252);
    line-height: 1.4;
  }
  .hsw-waitlist-actions {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    gap: 0.6rem;
    flex-shrink: 0;
  }
  .hsw-position-badge {
    background: #fef3c7;
    color: #92400e;
    border: 1px solid #fde68a;
    padding: 0.35rem 0.75rem;
    border-radius: 99px;
    font-size: 0.8rem;
    font-weight: 600;
    white-space: nowrap;
    letter-spacing: 0.01em;
  }
  .hsw-cta-link {
    background: var(--yoga-primary, #0a0a0a);
    color: var(--yoga-primary-text, #ffffff);
    border: 1px solid var(--yoga-primary, #0a0a0a);
    border-radius: var(--yoga-radius, 0);
    padding: 0.5rem 1rem;
    font-size: 0.85rem;
    font-weight: 500;
    cursor: pointer;
    white-space: nowrap;
    text-decoration: none;
    transition: opacity .2s, transform .15s;
    font-family: inherit;
    min-width: 8rem;
    display: inline-flex;
    align-items: center;
    justify-content: center;
  }
  .hsw-cta-link:hover {
    opacity: 0.9;
    transform: translateY(-1px);
  }
  .hsw-empty {
    padding: 3rem 1.5rem;
    text-align: center;
    color: var(--yoga-text-muted, #737373);
    background: var(--yoga-card-bg, #ffffff);
    border: 1px dashed var(--yoga-card-border, #e5e7eb);
    border-radius: var(--yoga-radius, 0);
    max-width: 900px;
    margin: 0 auto;
  }
  .hsw-empty-icon {
    font-size: 2.5rem;
    margin-bottom: 0.75rem;
    line-height: 1;
  }
  .hsw-empty-text {
    margin: 0;
    font-size: 0.95rem;
    color: var(--yoga-text-muted, #737373);
  }
  .hsw-error {
    background: #fef2f2;
    border: 1px solid #fecaca;
    border-radius: var(--yoga-radius, 0);
    padding: 1rem 1.25rem;
    color: #dc2626;
    font-size: 0.9rem;
    display: flex;
    align-items: center;
    gap: 0.6rem;
    max-width: 900px;
    margin: 0 auto;
  }
  .hsw-error-icon {
    font-size: 1.1rem;
    line-height: 1;
  }
  .hsw-skeleton-card {
    background: var(--yoga-card-bg, #ffffff);
    border: 1px solid var(--yoga-card-border, #e5e7eb);
    border-radius: var(--yoga-radius, 0);
    padding: 1.5rem;
  }
  .hsw-skeleton {
    background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
    background-size: 200% 100%;
    animation: hsw-shimmer 1.5s infinite;
    border-radius: 4px;
  }
  .hsw-sk-meta { height: 12px; width: 35%; margin-bottom: 0.6rem; }
  .hsw-sk-name { height: 20px; width: 55%; margin-bottom: 0.6rem; }
  .hsw-sk-desc { height: 14px; width: 80%; }
  @keyframes hsw-shimmer {
    0% { background-position: 200% 0; }
    100% { background-position: -200% 0; }
  }
  @media (max-width: 600px) {
    .hsw-waitlist-card {
      flex-direction: column;
      gap: 1rem;
    }
    .hsw-waitlist-actions {
      align-items: stretch;
      width: 100%;
    }
    .hsw-position-badge {
      text-align: center;
    }
    .hsw-cta-link {
      width: 100%;
    }
  }
`
